import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';

import { REGEX } from '@hsc/common/constants/regex';
import { AxiomQuoteLambdaResponse } from '@hsc/common/schemas/axiom.schema';
import { EcifProductType } from '@hsc/common/schemas/ecif.schema.js';
import { PcCoverage, PcCoveredLocation, PcPolicyHolder } from '@hsc/common/schemas/pc-common.schema.js';
import { PcPolicyNumberLambdaResponse } from '@hsc/common/schemas/pc-management.schema';
import { PcSubmissionNumberLambdaResponse } from '@hsc/common/schemas/pc-submission.schema';
import type { GisSuggestion } from '@components/quoting-flow/types/gis-types';
import { NotificationData } from '@constants/user-notifications';
import { DeveloperToolsService } from '@services/developer-tools.service';
import { FullStoryService } from '@services/fullstory.service';
import { boolToYesNo, checkFieldInvalid, YesNo } from '@utilities/form-utilities';
import { getPrimaryPolicyHolder, isEquipmentBreakdownEnabled } from '@utilities/policy-utilities';
import { scrollToTop } from '@utilities/window-utilities';

import {
  AxiomQuoteState,
  CLIENT_LOOKUP_TYPES,
  ClientLookupType,
  NotificationState,
  QUOTING_FLOW_STEP_ORDER,
  QuotingFlowField,
  QuotingFlowStep,
} from '../types/quoting-flow.types';

const INITIAL_AXIOM_QUOTE_STATE: AxiomQuoteState = {
  id: undefined,
  number: undefined,
  monthlyPrice: undefined,
  enrollmentFee: undefined,
  emailToClient: false,
  emailSent: false,
};

/*
 * Owns all navigation, state, form data, errors, and corresponding helpers
 * Note: Not provided in root, provided only in QuotingFlow component
 */
@Injectable()
export class QuotingFlowStore {
  // Services
  private readonly formBuilder = inject(FormBuilder);
  private readonly developerToolsService = inject(DeveloperToolsService);
  private readonly fullStoryService = inject(FullStoryService);

  // Properties
  // -------------------------------
  // Quoting flow
  // -------------------------------
  readonly step = signal<QuotingFlowStep>('CLIENT_LOOKUP');
  readonly percentage = computed<10 | 33 | 66 | 90>(() => {
    // If a quote is successful, remain at 90% regardless of step
    if (this.axiomQuoteState().id) return 90;

    // If on the final step, show 66% until quote is successful
    if (this.step() === 'QUOTE_REVIEW') return 66;

    // If on the property review step, show 66% if form is valid and no notification
    // Note that disabling the form will invalidate its status
    if (this.step() === 'PROPERTY_REVIEW') {
      const formPassing = this.propertyReviewFormStatus() === 'VALID' && !this.notificationState();
      return formPassing ? 66 : 33;
    }

    return this.isLookupSuccessful() ? 33 : 10;
  });
  readonly notificationState = signal<NotificationState | null>(null);

  // -------------------------------
  // 1) Client lookup
  // -------------------------------
  readonly clientLookupForm = this.formBuilder.nonNullable.group({
    lookupType: this.formBuilder.nonNullable.control<ClientLookupType>(
      CLIENT_LOOKUP_TYPES.POLICY_NUMBER,
      Validators.required,
    ),
    lookupValue: this.formBuilder.nonNullable.control<string>('', [
      Validators.required,
      Validators.pattern(REGEX.policyNumber),
    ]),
  });
  readonly isLookupTypePolicy = computed(() => this.lookupTypeValue() === CLIENT_LOOKUP_TYPES.POLICY_NUMBER);
  // Need a signal for computed isLookupTypePolicy to react to changes
  private readonly lookupTypeValue = toSignal(this.clientLookupForm.controls.lookupType.valueChanges, {
    initialValue: this.clientLookupForm.controls.lookupType.value,
  });
  readonly isLookupSuccessful = signal(false);

  // -------------------------------
  // 2) Property review
  // -------------------------------
  readonly propertyReviewForm = this.formBuilder.nonNullable.group({
    customer: this.formBuilder.nonNullable.group({
      addressLine1: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
      addressLine2: this.formBuilder.control<string | undefined>(undefined),
      city: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
      country: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
      emailAddress: this.formBuilder.nonNullable.control<string>('', [
        Validators.required,
        Validators.pattern(REGEX.email),
      ]),
      firstName: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
      lastName: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
      stateProvinceRegion: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
      zipPostalCode: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
    }),
    property: this.formBuilder.nonNullable.group({
      addressLine1: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
      addressLine2: this.formBuilder.control<string | undefined>(undefined),
      city: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
      country: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
      stateProvinceRegion: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
      zipPostalCode: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
    }),
    hasEquipmentBreakdown: this.formBuilder.nonNullable.control<YesNo | undefined>(undefined, [Validators.required]),
    isMobileOrManufactured: this.formBuilder.nonNullable.control<YesNo | undefined>(undefined, [Validators.required]),
    isRegisteredHistoric: this.formBuilder.nonNullable.control<YesNo | undefined>(undefined, [Validators.required]),
    isTownhouse: this.formBuilder.nonNullable.control<YesNo | undefined>(undefined, [Validators.required]),
    mainAgencyCode: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
    mailingAddress: this.formBuilder.nonNullable.control<string>(''),
    propertySquareFootage: this.formBuilder.nonNullable.control<number | undefined>(undefined, [
      Validators.required,
      Validators.min(1),
    ]),
    propertyType: this.formBuilder.nonNullable.control<EcifProductType | undefined>(undefined, [Validators.required]),
    servicingAgencyCode: this.formBuilder.nonNullable.control<string>('', [Validators.required]),
  });
  // Need a signal for computed percentage to react to changes
  private readonly propertyReviewFormStatus = toSignal(this.propertyReviewForm.statusChanges, {
    initialValue: this.propertyReviewForm.status,
  });
  // Retained across steps so navigating forward/back preserves mailing address entry
  readonly isDifferentMailingAddress = signal(false);
  readonly suggestedAddresses = signal<GisSuggestion[]>([]);
  readonly matchedAddresses = signal<string[]>([]);

  // -------------------------------
  // 3) Quote review
  // -------------------------------
  readonly axiomQuoteState = signal<AxiomQuoteState>(INITIAL_AXIOM_QUOTE_STATE);

  // -------------------------------
  // Confirmation modal
  // -------------------------------
  readonly showConfirmationModal = signal<boolean>(false);

  // ──────────────────────────────────────────────────────────
  // Navigation & Flow Management
  // ──────────────────────────────────────────────────────────
  setNotificationState(notification: NotificationData | null, ...invalidFields: QuotingFlowField[]): void {
    // If notification is null, clear notification state and invalid fields
    if (!notification) {
      this.notificationState.set(null);
      return;
    }
    // Sets the notification state with optional related invalid fields
    this.notificationState.set({
      notification,
      invalidFields: invalidFields.length ? invalidFields : undefined,
    });
  }

  handleForward(): void {
    this.setNotificationState(null);
    const currentStep = this.step();

    // Prevent advancement past last step
    if (currentStep === 'QUOTE_REVIEW') {
      this.showConfirmationModal.set(true);
      return;
    }

    const nextStep = QUOTING_FLOW_STEP_ORDER[QUOTING_FLOW_STEP_ORDER.indexOf(currentStep) + 1];
    if (nextStep) this.step.set(nextStep);
    scrollToTop();
  }

  handleBackward(): void {
    this.setNotificationState(null);
    const currentStep = this.step();

    // Prevent going back past first step
    if (currentStep === 'CLIENT_LOOKUP') return;

    const prevStep = QUOTING_FLOW_STEP_ORDER[QUOTING_FLOW_STEP_ORDER.indexOf(currentStep) - 1];
    if (prevStep === 'CLIENT_LOOKUP') {
      // Reset everything if returning to step 1
      this.fullStoryService.event('Quote Flow Reset');
      this.resetQuotingFlow();
      scrollToTop();
    } else {
      this.step.set(prevStep);
    }
  }

  resetClientLookupStep(all: boolean): void {
    if (all) this.clientLookupForm.reset();
    this.clientLookupForm.enable(); // Disabled on successful client lookup
    this.isLookupSuccessful.set(false);
  }

  resetPropertyReviewStep(): void {
    this.propertyReviewForm.reset();
    this.propertyReviewForm.enable(); // Disabled on successful axiom quote creation
    this.isDifferentMailingAddress.set(false);
    this.suggestedAddresses.set([]);
    this.matchedAddresses.set([]);
  }

  resetAxiomQuoteStep(): void {
    this.axiomQuoteState.set(INITIAL_AXIOM_QUOTE_STATE);
  }

  resetQuotingFlow(): void {
    this.step.set('CLIENT_LOOKUP');
    this.setNotificationState(null);
    this.resetClientLookupStep(true);
    this.resetPropertyReviewStep();
    this.resetAxiomQuoteStep();
  }

  // ──────────────────────────────────────────────────────────
  // API Response Mapping
  // ──────────────────────────────────────────────────────────
  private patchPropertyReviewForm(source: {
    coveredLocation: PcCoveredLocation;
    policyHolders: PcPolicyHolder[];
    mainAgencyCode: string | null;
    productType: EcifProductType;
    agencyCode: string | null;
    coverages: PcCoverage[];
  }): void {
    const holder = getPrimaryPolicyHolder(source.policyHolders);
    const loc = source.coveredLocation.location;

    this.propertyReviewForm.patchValue({
      customer: {
        addressLine1: loc.addressLine1 ?? '',
        addressLine2: loc.addressLine2 || undefined,
        city: loc.city ?? '',
        country: loc.country ?? '',
        emailAddress: holder?.emailAddress ?? '',
        firstName: holder?.person.firstName ?? '',
        lastName: holder?.person.lastName ?? '',
        stateProvinceRegion: loc.state ?? '',
        zipPostalCode: loc.postalCode ?? '',
      },
      property: {
        addressLine1: loc.addressLine1 ?? '',
        addressLine2: loc.addressLine2 || undefined,
        city: loc.city ?? '',
        country: loc.country ?? '',
        stateProvinceRegion: loc.state ?? '',
        zipPostalCode: loc.postalCode ?? '',
      },
      hasEquipmentBreakdown: boolToYesNo(isEquipmentBreakdownEnabled(source.coverages)),
      // isMobileOrManufactured isn't stored but a UI check
      isRegisteredHistoric: boolToYesNo(source.coveredLocation.registeredHistoric),
      isTownhouse: boolToYesNo(source.coveredLocation.townhouse),
      mainAgencyCode: source.mainAgencyCode ?? '',
      propertySquareFootage: source.coveredLocation.constructionInfo.squareFootage ?? undefined,
      propertyType: source.productType,
      servicingAgencyCode: source.agencyCode ?? '',
    });

    // Clear the email address for manual testing
    if (this.developerToolsService.emailOverride()) {
      this.propertyReviewForm.controls.customer.controls.emailAddress.setValue('');
    }
  }

  patchFromPolicyLookup(r: PcPolicyNumberLambdaResponse): void {
    this.patchPropertyReviewForm({
      coveredLocation: r.data.coveredLocation,
      policyHolders: r.data.policyHolders,
      mainAgencyCode: r.data.mainAgencyCode,
      productType: r.data.productType,
      agencyCode: r.data.policy.producer.agencyCode,
      coverages: r.data.coverages.coverages,
    });
  }

  patchFromSubmissionLookup(r: PcSubmissionNumberLambdaResponse): void {
    this.patchPropertyReviewForm({
      coveredLocation: r.data.quote.coveredLocation,
      policyHolders: r.data.quote.policyHolders,
      mainAgencyCode: r.data.mainAgencyCode,
      productType: r.data.productType,
      agencyCode: r.data.quote.producer.agencyCode,
      coverages: r.data.quote.offeredQuotes[0].coverages ?? [],
    });
  }

  patchFromAxiomQuote(response: AxiomQuoteLambdaResponse): void {
    this.axiomQuoteState.update((prev) => ({
      ...prev,
      id: response.data.quoteId,
      number: response.data.quoteNumber,
      monthlyPrice: response.data.monthlyPrice,
      enrollmentFee: response.data.enrollmentFee,
    }));
  }

  isFieldInvalid(fieldName: QuotingFlowField, variant: 'view' | 'editable'): string | boolean | null {
    return checkFieldInvalid(this.notificationState()?.invalidFields, fieldName, variant);
  }
}
